import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';

export type BucketCount = { key: string; count: number };
export type MonthlyEventCount = { month: string; count: number };
export type MonthlyUserCount = { month: string; count: number };

export type MonthlyEventCountBySport = {
  sportId: string;
  sportName: string;
  sportNameFr?: string;
  sportNameEn?: string;
  months: MonthlyEventCount[];
};

export type MonthlyUserCountBySport = {
  sportId: string;
  sportName: string;
  sportNameFr?: string;
  sportNameEn?: string;
  months: MonthlyUserCount[];
};

export type SportParticipationReportItem = {
  sportId: string;
  sportName?: string;
  sportNameFr?: string;
  sportNameEn?: string;
  totalParticipants: number;
  totalEvents: number;
  totalUsers: number;
  gender: BucketCount[];
  age: BucketCount[];
  level: BucketCount[];
};

export type SportDefinition = {
  sportId: string;
  slug: string;
  icon: string;
  name: string;
};

export type UserStatusSummary = {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
};

export type SportParticipationReportResponse = {
  sports: SportDefinition[];
  items: SportParticipationReportItem[];
  eventsByMonth: MonthlyEventCount[];
  eventsByMonthPerSport: MonthlyEventCountBySport[];
  userSummary: UserStatusSummary;
  usersByMonthPerSport: MonthlyUserCountBySport[];
  genderSummary: BucketCount[];
  ageSummary: BucketCount[];
  usersWithoutSport: number;
};

export type UserStatusFilter = 'all' | 'active' | 'inactive';
export type GenderFilter = 'all' | 'male' | 'female';
export type LevelFilter = 'all' | '1' | '2' | '3';

@Injectable({ providedIn: 'root' })
export class ReportStoreService {
  #http = inject(HttpClient);

  /** Year-over-year: the same request, dates shifted one year back. */
  #loadComparison(
    startDate?: string | null,
    endDate?: string | null,
    selectedSports?: string[] | null,
    userStatus?: UserStatusFilter | null,
    gender?: GenderFilter | null,
    city?: string | null,
    level?: LevelFilter | null,
  ): void {
    this.compare.set(null);
    if (!startDate || !endDate) {
      return;
    }
    const shift = (iso: string) => `${Number(iso.slice(0, 4)) - 1}${iso.slice(4)}`;
    let params = new HttpParams().set('startDate', shift(startDate)).set('endDate', shift(endDate));
    if (selectedSports?.length) params = params.set('sports', selectedSports.join(','));
    if (userStatus && userStatus !== 'all') params = params.set('userStatus', userStatus);
    if (gender && gender !== 'all') params = params.set('gender', gender);
    if (city?.trim()) params = params.set('city', city.trim());
    if (level && level !== 'all') params = params.set('level', level);

    this.#http
      .get<SportParticipationReportResponse>(
        `${environment.api}/api/v1/admin/reports/sport-participation`,
        { params },
      )
      .subscribe({
        next: (res) => {
          if (!res) {
            return;
          }
          this.compare.set({
            users: res.userSummary?.totalUsers ?? 0,
            events: (res.eventsByMonth ?? []).reduce((sum, m) => sum + (m.count || 0), 0),
            participants: (res.items ?? []).reduce((sum, i) => sum + (i.totalParticipants || 0), 0),
          });
        },
        // Without last year the page simply shows no comparison.
        error: () => this.compare.set(null),
      });
  }

  loading = signal(false);
  sportDefinitions = signal<SportDefinition[]>([]);
  allItems = signal<SportParticipationReportItem[]>([]);
  filteredItems = signal<SportParticipationReportItem[]>([]);
  eventsByMonth = signal<MonthlyEventCount[]>([]);
  eventsByMonthPerSport = signal<MonthlyEventCountBySport[]>([]);
  userSummary = signal<UserStatusSummary>({ totalUsers: 0, activeUsers: 0, inactiveUsers: 0 });
  usersByMonthPerSport = signal<MonthlyUserCountBySport[]>([]);
  genderSummary = signal<BucketCount[]>([]);
  ageSummary = signal<BucketCount[]>([]);
  usersWithoutSport = signal(0);

  /** The same window one year earlier, reduced to its headline figures.
   * Null while no comparison is possible (open-ended range, or request lost). */
  compare = signal<{ users: number; events: number; participants: number } | null>(null);

  loadAllSportParticipationReport(
    startDate?: string | null,
    endDate?: string | null,
    selectedSports?: string[] | null,
    userStatus?: UserStatusFilter | null,
    gender?: GenderFilter | null,
    city?: string | null,
    level?: LevelFilter | null,
  ): void {
    this.loading.set(true);

    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    if (selectedSports?.length) params = params.set('sports', selectedSports.join(','));
    if (userStatus && userStatus !== 'all') params = params.set('userStatus', userStatus);
    if (gender && gender !== 'all') params = params.set('gender', gender);
    if (city?.trim()) params = params.set('city', city.trim());
    if (level && level !== 'all') params = params.set('level', level);

    this.#loadComparison(startDate, endDate, selectedSports, userStatus, gender, city, level);

    this.#http
      .get<SportParticipationReportResponse>(
        `${environment.api}/api/v1/admin/reports/sport-participation`,
        { params },
      )
      .subscribe({
        next: (res) => {
          this.loading.set(false);
          this.sportDefinitions.set(res?.sports ?? []);

          const items = (res?.items ?? []).filter(
            (item) =>
              item &&
              typeof item.sportName === 'string' &&
              item.sportName.trim().length > 0,
          );

          this.allItems.set(items);
          this.filteredItems.set(items);
          this.eventsByMonth.set(res?.eventsByMonth ?? []);
          this.eventsByMonthPerSport.set(res?.eventsByMonthPerSport ?? []);
          this.userSummary.set(
            res?.userSummary ?? { totalUsers: 0, activeUsers: 0, inactiveUsers: 0 },
          );
          this.usersByMonthPerSport.set(res?.usersByMonthPerSport ?? []);
          this.genderSummary.set(res?.genderSummary ?? []);
          this.ageSummary.set(res?.ageSummary ?? []);
          this.usersWithoutSport.set(res?.usersWithoutSport ?? 0);
        },
        error: (err) => {
          console.error('Failed to load sport participation report', err);
          this.loading.set(false);
          this.sportDefinitions.set([]);
          this.allItems.set([]);
          this.filteredItems.set([]);
          this.eventsByMonth.set([]);
          this.eventsByMonthPerSport.set([]);
          this.userSummary.set({ totalUsers: 0, activeUsers: 0, inactiveUsers: 0 });
          this.usersByMonthPerSport.set([]);
          this.genderSummary.set([]);
          this.ageSummary.set([]);
          this.usersWithoutSport.set(0);
        },
      });
  }

  filterBySports(selectedIds: string[] | null | undefined): void {
    const ids = selectedIds ?? [];
    this.filteredItems.set(
      ids.length === 0
        ? this.allItems()
        : this.allItems().filter((i) => ids.includes(i.sportId)),
    );
  }
}